package no.vareflow.web

import no.vareflow.app.domain.*

// Company logo in the browser: reading an uploaded image (Mer → Bedrift) and showing the saved one.

private var cachedLogo: CompanyLogo? = null
private var cachedLogoUrl = ""

/** data: URL of the logo's PNG, made once per logo (the page is redrawn often). */
fun logoDataUrl(logo: CompanyLogo): String {
    if (logo !== cachedLogo) {
        cachedLogo = logo
        cachedLogoUrl = "data:image/png;base64," + Crypto.base64Encode(logo.png)
    }
    return cachedLogoUrl
}

/**
 * Reads an image file chosen by the admin: trims empty margins, scales the longest side to
 * [CompanyLogo.MAX_SIDE] and makes the PNG (screens) and the JPEG (reports). A light logo on a
 * transparent background (white text) gets the dark background.
 */
fun readLogoFile(file: dynamic, onResult: (Result<CompanyLogo>) -> Unit) {
    val fail = { onResult(Result.failure(IllegalStateException(t("Bildet kunne ikke leses. Bruk PNG eller JPG.", "The image could not be read. Use PNG or JPG.")))) }
    val url = window.URL.createObjectURL(file)
    val img = document.createElement("img")
    img.onload = {
        try {
            window.URL.revokeObjectURL(url)
            onResult(Result.success(processLogo(img)))
        } catch (e: Throwable) {
            fail()
        }
    }
    img.onerror = { window.URL.revokeObjectURL(url); fail() }
    img.src = url
}

private fun processLogo(img: dynamic): CompanyLogo {
    val w0 = (img.naturalWidth as Number).toInt()
    val h0 = (img.naturalHeight as Number).toInt()
    if (w0 <= 0 || h0 <= 0) throw IllegalStateException("empty image")
    // 1. Draw at most 1024 px wide/high and look at the pixels.
    val s1 = minOf(1.0, 1024.0 / maxOf(w0, h0))
    val w1 = maxOf(1, (w0 * s1).toInt())
    val h1 = maxOf(1, (h0 * s1).toInt())
    val work = document.createElement("canvas")
    work.width = w1
    work.height = h1
    val wctx = work.getContext("2d")
    wctx.drawImage(img, 0, 0, w1, h1)
    val data = wctx.getImageData(0, 0, w1, h1).data
    val total = w1 * h1
    var transparent = 0; var visible = 0; var light = 0
    for (i in 0 until total) {
        val a = (data[i * 4 + 3] as Number).toInt()
        if (LogoAnalysis.isTransparent(a)) transparent++
        if (LogoAnalysis.isVisible(a)) {
            visible++
            if (LogoAnalysis.isLight((data[i * 4] as Number).toInt(), (data[i * 4 + 1] as Number).toInt(), (data[i * 4 + 2] as Number).toInt())) light++
        }
    }
    val transparentBackground = LogoAnalysis.hasTransparentBackground(transparent, total)
    val dark = LogoAnalysis.needsDarkBackground(transparent, visible, light, total)
    // 2. The content box (white text on a transparent logo is content, not margin).
    var minX = w1; var minY = h1; var maxX = -1; var maxY = -1
    for (y in 0 until h1) {
        for (x in 0 until w1) {
            val i = (y * w1 + x) * 4
            val empty = LogoAnalysis.isEmpty((data[i + 3] as Number).toInt(), (data[i] as Number).toInt(),
                (data[i + 1] as Number).toInt(), (data[i + 2] as Number).toInt(), transparentBackground)
            if (!empty) {
                if (x < minX) minX = x
                if (x > maxX) maxX = x
                if (y < minY) minY = y
                if (y > maxY) maxY = y
            }
        }
    }
    if (maxX < 0) { minX = 0; minY = 0; maxX = w1 - 1; maxY = h1 - 1 }
    val pad = maxOf(2, (maxOf(maxX - minX, maxY - minY) * 0.03).toInt())
    minX = maxOf(0, minX - pad); minY = maxOf(0, minY - pad)
    maxX = minOf(w1 - 1, maxX + pad); maxY = minOf(h1 - 1, maxY + pad)
    val cw = maxX - minX + 1
    val ch = maxY - minY + 1
    // 3. Scale the cropped part to the final size.
    val s2 = minOf(1.0, CompanyLogo.MAX_SIDE.toDouble() / maxOf(cw, ch))
    val w = maxOf(1, (cw * s2).toInt())
    val h = maxOf(1, (ch * s2).toInt())
    val out = document.createElement("canvas")
    out.width = w
    out.height = h
    val ctx = out.getContext("2d")
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = "high"
    ctx.drawImage(work, minX, minY, cw, ch, 0, 0, w, h)
    val png = dataUrlBytes(out.toDataURL("image/png") as String)
    return CompanyLogo(png, jpegOn(out, w, h, dark), w, h, dark)
}

/** JPEG for reports: the picture on white, or on the dark badge colour for light logos. */
private fun jpegOn(canvas: dynamic, w: Int, h: Int, dark: Boolean): ByteArray {
    val ctx = canvas.getContext("2d")
    ctx.globalCompositeOperation = "destination-over"
    ctx.fillStyle = if (dark) CompanyLogo.DARK_BACKGROUND_CSS else "#FFFFFF"
    ctx.fillRect(0, 0, w, h)
    ctx.globalCompositeOperation = "source-over"
    return dataUrlBytes(canvas.toDataURL("image/jpeg", 0.9) as String)
}

/** The same logo with the other background (Bedriftsprofil → "Bakgrunn"): a new report JPEG. */
fun logoWithBackground(logo: CompanyLogo, dark: Boolean, onResult: (Result<CompanyLogo>) -> Unit) {
    val img = document.createElement("img")
    img.onload = {
        try {
            val c = document.createElement("canvas")
            c.width = logo.width
            c.height = logo.height
            c.getContext("2d").drawImage(img, 0, 0, logo.width, logo.height)
            onResult(Result.success(CompanyLogo(logo.png, jpegOn(c, logo.width, logo.height, dark), logo.width, logo.height, dark)))
        } catch (e: Throwable) {
            onResult(Result.failure(IllegalStateException(t("Bildet kunne ikke leses.", "The image could not be read."))))
        }
    }
    img.onerror = { onResult(Result.failure(IllegalStateException(t("Bildet kunne ikke leses.", "The image could not be read.")))) }
    img.src = "data:image/png;base64," + Crypto.base64Encode(logo.png)
}

private fun dataUrlBytes(dataUrl: String): ByteArray =
    Crypto.base64Decode(dataUrl.substringAfter(',')) ?: throw IllegalStateException("bad data url")
